"use client";

import { ClipboardList } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { buildAffiliateReferralUrl } from "@/features/affiliate/shared/referralUrl";
import type { AffiliateForClient } from "@/features/affiliate/shared/types";
import { useRouter } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { useSession } from "@/lib/client/auth/client";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { useLoadingStore } from "@/lib/client/stores/useLoadingStore";
import { fetcher, HttpResponseError } from "@/lib/http/fetcher";
import { affiliateWithdrawalLimit, BaseUrl, baseCommissionRate } from "@/lib/shared/constants";
import type { OrderForClient } from "@/lib/shared/types/interfaces";
import { OrderStatusEnum } from "@/lib/shared/types/OrderStatusEnum";
import { getTotal } from "@/lib/shared/utils/calculatePrice";
import {
  BackNav,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Container,
  Grid,
  HStack,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  VStack,
} from "@/ui";

type AffiliateResponse = {
  affiliate: AffiliateForClient | null;
  orders: OrderForClient[];
};

export const AffiliateContainer = () => {
  const router = useRouter();
  const { country } = useCountryStore();
  const toastT = useTranslations("toast");
  const generalT = useTranslations("general");
  const affiliateT = useTranslations("affiliate");
  const { data: session } = useSession();
  const user = session?.user;
  const sessionAffiliate = (
    session?.user as { affiliate?: { affiliateId?: string } | null } | undefined
  )?.affiliate;
  const sessionAffiliateId = sessionAffiliate?.affiliateId;
  const [affiliateID, setAffiliateID] = useState<string>();
  const [affiliate, setAffiliate] = useState<AffiliateForClient>();
  const [orders, setOrders] = useState<OrderForClient[]>();
  const [affiliateIDError, setAffiliateIDError] = useState(false);
  const { isLoading, setIsLoading } = useLoadingStore();
  const [isPayoutPending, setIsPayoutPending] = useState(false);
  const affiliateReferralUrl = affiliateID
    ? buildAffiliateReferralUrl(BaseUrl, affiliateID)
    : BaseUrl;

  const handleIDCreation = async () => {
    setIsLoading(true);
    try {
      if (!affiliateID) {
        toast.error(toastT("error.affiliate_unique"));
        return;
      }

      const affiliate = await fetcher<AffiliateForClient>("/api/affiliate", {
        method: "POST",
        body: JSON.stringify({ affiliateId: affiliateID }),
      });
      setAffiliate(affiliate);
      toast.success(toastT("success.affiliate_creation"));
    } catch (error) {
      if (error instanceof HttpResponseError && error.status === 409) {
        toast.error(toastT("error.affiliate_unique"));
      } else {
        toast.error(error instanceof Error ? error.message : toastT("error.affiliate_unique"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!affiliateID) return;
    await navigator.clipboard.writeText(affiliateReferralUrl);
    trackEvent(AnalyticsEvents.AFFILIATE_LINK_COPIED, { source: "affiliate_portal" });
    toast.success(toastT("success.copy"));
  };

  const handleWithdraw = async () => {
    if (affiliate?.payoutMethod?.value) {
      setIsLoading(true);
      try {
        const updatedAffiliate = await fetcher<AffiliateForClient>("/api/affiliate", {
          method: "PATCH",
          body: JSON.stringify({
            operation: "requestPayout",
            affiliateId: affiliate.affiliateId,
          }),
        });

        setAffiliate(updatedAffiliate);
        setIsPayoutPending(true);
        toast.success(toastT("success.withdrawal"));
      } catch (error) {
        toast.error(error instanceof Error ? error.message : toastT("error.payout_method"));
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.error(toastT("error.payout_method"));
    }
  };

  const handlePayoutMethodUpdate = async () => {
    if (!affiliate?.payoutMethod) return;
    setIsLoading(true);
    try {
      const updatedAffiliate = await fetcher<AffiliateForClient>("/api/affiliate", {
        method: "PATCH",
        body: JSON.stringify({
          operation: "updatePayoutMethod",
          affiliateId: affiliate.affiliateId,
          payoutMethod: affiliate.payoutMethod,
        }),
      });
      setAffiliate(updatedAffiliate);
      toast.success(toastT("success.payout_method_update"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : toastT("error.payout_method"));
    } finally {
      setIsLoading(false);
    }
  };

  const userInit = async () => {
    if (!sessionAffiliateId) {
      setAffiliate(undefined);
      setOrders(undefined);
      return;
    }

    setIsLoading(true);
    try {
      setAffiliateID(sessionAffiliateId);
      const data = await fetcher<AffiliateResponse>("/api/affiliate");
      setAffiliate(data.affiliate ?? undefined);
      setOrders(data.orders);
      setIsPayoutPending(Boolean(data.affiliate?.isPendingPayment));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load affiliate data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void userInit();
  }, [sessionAffiliateId]);

  if (!user) return null;

  return (
    <Container>
      <BackNav onBack={() => router.back()} />
      <Grid className={"grid-cols-1 gap-8 sm:grid-cols-2"}>
        {affiliate && (
          <Card>
            <CardHeader>
              <CardTitle>{affiliateT("balance")}</CardTitle>
              <CardDescription />
            </CardHeader>
            <CardContent>
              <HStack className={"flex-wrap items-center space-x-2 space-y-2"}>
                <Text className="font-price text-6xl">
                  {country.symbol}
                  {Number(affiliate.commission * country.exchangeRate).toFixed(2)}
                </Text>
                <Button
                  disabled={
                    affiliate.commission < affiliateWithdrawalLimit ||
                    affiliate.isPendingPayment ||
                    isPayoutPending
                  }
                  onClick={handleWithdraw}
                >
                  {isPayoutPending ? affiliateT("processing_payout") : affiliateT("withdraw")}
                </Button>
              </HStack>
            </CardContent>
          </Card>
        )}
        {affiliate && (
          <Card>
            <CardHeader>
              <CardTitle>{affiliateT("payout_title")}</CardTitle>
              <CardDescription>{affiliateT("payout_message")}</CardDescription>
            </CardHeader>
            <CardContent>
              <HStack className={"flex-wrap"}>
                <Select
                  defaultValue={affiliate.payoutMethod?.name}
                  onValueChange={(value) => {
                    setAffiliate({
                      ...affiliate,
                      payoutMethod: {
                        ...affiliate.payoutMethod,
                        name: value,
                        value: affiliate.payoutMethod?.value ?? "",
                      },
                    });
                  }}
                >
                  <SelectTrigger className="w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iban">IBAN</SelectItem>
                    <SelectItem value="paypal">Paypal</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  className={"min-w-[120px] flex-1"}
                  value={affiliate.payoutMethod?.value ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    setAffiliate({
                      ...affiliate,
                      payoutMethod: {
                        ...affiliate.payoutMethod,
                        name: affiliate.payoutMethod?.name ?? "iban",
                        value,
                      },
                    });
                  }}
                />
                <Button
                  disabled={isLoading}
                  onClick={handlePayoutMethodUpdate}
                >
                  {generalT("confirm")}
                </Button>
              </HStack>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>{affiliateT("id_title")}</CardTitle>
          </CardHeader>
          <CardContent>
            {affiliate?.affiliateId ? (
              <Text className={"text-lg"}>{affiliate.affiliateId}</Text>
            ) : (
              <VStack className={"space-y-2"}>
                <Text className={"text-sm text-foreground/75"}>{affiliateT("id_hint")}</Text>
                <HStack>
                  <Input
                    className={"w-fit"}
                    value={affiliateID}
                    onChange={(event) => {
                      const value = event.target.value;
                      const pattern = /^[\w-_]*$/;
                      const validation = pattern.test(value);
                      setAffiliateID(value);
                      if (validation) {
                        setAffiliateIDError(false);
                      } else {
                        setAffiliateIDError(true);
                      }
                    }}
                  />
                  <Button
                    disabled={isLoading}
                    onClick={handleIDCreation}
                  >
                    {generalT("create")}
                  </Button>
                </HStack>
              </VStack>
            )}
            {affiliateIDError && (
              <Text className={"text-sm italic text-red-400"}>{affiliateT("id_error")}</Text>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{affiliateT("url_title")}</CardTitle>
            <CardDescription className={"text-foreground/80"}>
              {affiliateT("url_hint", { affiliateId: affiliateID ?? "" })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Text>
              {affiliateT("before")}
              {BaseUrl}
            </Text>
            <HStack className={"items-center space-x-2"}>
              <Text>{affiliateT("after")}</Text>
              <Text
                className={"w-fit cursor-pointer"}
                onClick={handleCopy}
              >
                {affiliateReferralUrl}
              </Text>
              <ClipboardList
                className={"h-6 w-6 cursor-pointer"}
                onClick={handleCopy}
              />
            </HStack>
          </CardContent>
        </Card>
      </Grid>
      <Card className={"mt-4"}>
        <CardHeader>
          <CardTitle>{affiliateT("orders_title")}</CardTitle>
          {affiliate && (
            <CardDescription className={"text-foreground/80"}>
              {affiliateT("orders_desc")}
              {affiliate.commissionRate * 100}%
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableCaption>
              {orders && orders.length > 0 ? (
                <Text>{affiliateT("orders_caption")}</Text>
              ) : (
                <Text className={"my-12 text-xl"}>{affiliateT("no_orders")}</Text>
              )}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{affiliateT("table_id")}</TableHead>
                <TableHead>{affiliateT("table_status")}</TableHead>
                <TableHead>{affiliateT("table_price")}</TableHead>
                <TableHead className="text-right">{affiliateT("table_commission")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders?.map((order) => {
                const totalPrice = getTotal(order.items, country.exchangeRate);
                return (
                  <TableRow key={order.id}>
                    <TableCell>
                      <VStack>
                        <Text className="font-medium">{String(order.id)}</Text>
                        <Text>{order.createdAt || ""}</Text>
                      </VStack>
                    </TableCell>
                    <TableCell>{order.status}</TableCell>
                    <TableCell>
                      <span className="font-price">
                        {country.symbol}
                        {Number(totalPrice).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-price">
                        {country.symbol}
                        {order.status === OrderStatusEnum.Paid
                          ? Number(totalPrice * baseCommissionRate).toFixed(2)
                          : "0.00"}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Container>
  );
};
